import {pageAuthorizedByGroup} from "../../fragments/PageAuthorization";
import {Link, useParams} from "react-router-dom";
import {useQuery} from "@apollo/client";
import {graphQLServices} from "../../../services/GraphQLServices";
import React, {useState} from "react";
import {
	Box,
	Breadcrumbs,
	CircularProgress,
	Paper,
	Stack,
	TextField,
	Typography
} from "@mui/material";
import {cloneMutationData} from "../Utils";
import {PentaSplit} from "../../../components/common/PentaSplit";
import {ProcessButton} from "../../../components/common/input/ProcessButton";
import {EXISTS_PARTNER_HIERARCHY, GET_PARTNER_HIERARCHY, PUT_PARTNER_HIERARCHY} from "../Queries";
import {TabPanel} from "../../../components/common/TabPanel";
import {translateFunction} from "../../../localization/TranslationService";
import {productsEditorTargetStage} from "../ProductModuleOverview";
import {PartnerHierarchy} from "../models/PartnerHierarchy";

const translate = translateFunction("SureBasePortalProductsCatalog");

export const PartnerHierarchyDetails = pageAuthorizedByGroup(function () {
	const id = useParams()?.id;
	
	const {loading, data, error} = useQuery(GET_PARTNER_HIERARCHY, {
		client: graphQLServices.productsClient,
		fetchPolicy: "no-cache",
		variables: {
			stage: productsEditorTargetStage,
			id: id
		}
	});
	
	const [partnerHierarchy, setPartnerHierarchy] = useState<PartnerHierarchy>();
	
	if (loading)
		return <Box sx={{display: "flex", width: "100%", height: "100%"}}>
			<CircularProgress style={{margin: "auto"}}/>
		</Box>;
	
	if (error)
		throw error;
	
	const nodes = data.partnerHierarchy.nodes;
	if (nodes === null || nodes.length === 0)
		throw new Error(`Partner Hierarchy with id ${id} was not found.`);
	
	const node: PartnerHierarchy = nodes[0];
	if (!partnerHierarchy) {
		setPartnerHierarchy(node);
		return <Box sx={{display: "flex", width: "100%", height: "100%"}}>
			<CircularProgress style={{margin: "auto"}}/>
		</Box>;
	}
	
	
	const props = {partnerHierarchy, setPartnerHierarchy};
	
	const handleSave = async (item: PartnerHierarchy) => {
		item.clientName = item.clientName.toLowerCase().trim();
		item.parent = item.parent?.toLowerCase()?.trim() ?? null;
		await exists(item).then(async (x) => {
			if (x) {
				throw new Error(`Item already exists`);
			}
			
			await saveChanges(item);
		});
	};
	
	return <PentaSplit
		top={<Breadcrumbs style={{margin: "5px"}}>
			<Link style={{color: "inherit", textDecoration: "unset"}} to="/products">{translate("products")}</Link>
			<Link style={{color: "inherit", textDecoration: "unset"}} to="/products/partnerhierarchy">{translate("Partner Hierarchy")}</Link>
			<Typography color="text.primary">{id}</Typography>
		</Breadcrumbs>}
		center={<Paper style={{
			height: "100%",
		}}>
			<Stack style={{height: "100%"}}>
				<div style={{flexGrow: 1, overflow: "hidden"}}>
					<Information {...props}/>
				</div>
				<div style={{flexGrow: 0, padding: "10px"}}>
					<ProcessButton
						defaultText={translate("Save")}
						inProgressText={translate("Saving")}
						completedText={translate("Saved")}
						resetKeys={[partnerHierarchy]}
						variant={"outlined"}
						onClick={() => handleSave(partnerHierarchy)}/>
				</div>
			</Stack>
		</Paper>}
	/>;
}, ["Admin", "Translations"]);

async function exists(item: PartnerHierarchy): Promise<boolean> {
	
	const existsItems = await graphQLServices.productsClient.query(
		{
			query: EXISTS_PARTNER_HIERARCHY,
			variables: {
				stage: productsEditorTargetStage,
				clientName: item.clientName,
			},
			fetchPolicy: "no-cache"
		}
	);
	
	const nodes: any[] = existsItems.data.partnerHierarchy.nodes;
	return nodes.filter(x => x.id !== item.id).length > 0;
}

async function saveChanges(item: PartnerHierarchy) {
	await graphQLServices.productsClient.mutate({
		mutation: PUT_PARTNER_HIERARCHY,
		variables: {
			stage: productsEditorTargetStage,
			partnerHierarchy: cloneMutationData(item),
		}
	});
}

interface PartnerHierarchyProps {
	readonly partnerHierarchy: PartnerHierarchy;
	readonly setPartnerHierarchy: (partnerHierarchy: PartnerHierarchy) => void;
}

function Information(props: PartnerHierarchyProps) {
	return <TabPanel value={0} index={0}>
		<div style={{height: "100%", overflow: "auto"}}>
			<Stack spacing={2}>
				<div>{translate("Partner Hierarchy Information")}</div>
				<TextField
					label={translate("Client Name")}
					value={props.partnerHierarchy.clientName}
					variant={"standard"}
					onChange={event => props.setPartnerHierarchy({...props.partnerHierarchy, clientName: event.target.value})}
				/>
				<TextField
					label={translate("Parent")}
					value={props.partnerHierarchy.parent}
					variant={"standard"}
					onChange={event => props.setPartnerHierarchy({...props.partnerHierarchy, parent: event.target.value})}
				/>
			</Stack>
		</div>
	</TabPanel>;
}


export function openPartnerHierarchyDetailsFor(id: string) {
	window.location.href = linkPartnerHierarchyFor(id);
}

export function linkPartnerHierarchyFor(id: string): string {
	return `/products/partnerhierarchy/${encodeURIComponent(id)}`;
}
